import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/shared/logo";
import { UserMenu } from "@/components/shared/user-menu";
import { useOpenTaskCount } from "./journeys/use-open-task-count";
import { usePortalListing } from "./listing/use-portal-listing";

// Port of /components/portal/TopNav.tsx. The "Action plan" label maps to
// /portal/journeys — same UX rename the legacy nav used to avoid leaking
// internal jargon to partners.
//
// "My Listing" (0091) is the one data-gated item: it renders only when the
// partner is linked to a marketplace listing AND the marketplace connection
// is configured — hidden while loading, matching the goals-widget convention
// of hiding rather than flashing. The gate shares the page's query.
const NAV = [
  { href: "/portal/dashboard", label: "Dashboard" },
  { href: "/portal/analytics", label: "Analytics" },
  { href: "/portal/deals", label: "My deals" },
  { href: "/portal/resources", label: "Resources" },
  { href: "/portal/journeys", label: "Action plan" },
  { href: "/portal/mdf", label: "Marketing Funds" },
  { href: "/portal/listing", label: "My Listing" },
  { href: "/portal/onboarding", label: "Onboarding" },
  { href: "/portal/support", label: "Support" },
];

export function PortalTopNav({
  email,
  fullName,
}: {
  email: string;
  fullName: string | null;
}) {
  const { pathname } = useLocation();
  const { data: openTaskCount } = useOpenTaskCount();
  const { data: listing } = usePortalListing();
  const showListing = listing?.configured === true && listing.linked === true;
  const nav = NAV.filter((item) => item.href !== "/portal/listing" || showListing);
  return (
    <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Link to="/portal/dashboard">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {nav.map((item) => {
              const active = pathname.startsWith(item.href);
              const badge =
                item.href === "/portal/journeys" && (openTaskCount ?? 0) > 0
                  ? openTaskCount
                  : null;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  {item.label}
                  {badge != null ? (
                    <span
                      className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground"
                      aria-label={`${badge} open ${badge === 1 ? "task" : "tasks"}`}
                    >
                      {badge > 99 ? "99+" : badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </div>
        <UserMenu email={email} fullName={fullName} role="partner" />
      </div>
    </header>
  );
}
